import React, { useState, useEffect } from "react";
import { Modal, ModalBody } from "reactstrap";
import BookingDetailPdf from "./booking-detail-pdf";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { getBookingDetails } from "../../../actions/admin/scheduled-event-actions";
import { connect } from "react-redux";
import { notification, onModalOpen } from "../../../utils/utility";
import Loader from "../../../components/Loader/Loader";
import CancelModal from "./cancel-modal";
import RescheduleModal from "./reschedule-modal";
import { useNavigate } from "react-router-dom";
import "../schedule/scheduled-events/scheduledEvents.scss";

// ─── DetailItem — matches se-details__item in scheduledEvents.scss ───────────
const DetailItem = ({ label, value, wide = false }) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  return (
    <div className={`se-details__item${wide ? " se-details__item--wide" : ""}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const ViewDetailsModal = (props) => {
  const { getBookingDetails, bookingId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const [showCancelModal, setShowCancelModal]       = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    setIsLoading(true);
    getBookingDetails({
      data: { id: bookingId },
      onSuccess: (response) => { setData(response); setIsLoading(false); },
      onError:   ()         => { setIsLoading(false); },
    });
  }, [bookingId]);

  const hideCancelModal      = () => setShowCancelModal(false);
  const hideRescheduleModal  = () => setShowRescheduleModal(false);

  /** Copy event summary to clipboard as rich HTML */
  const onCopyEventSummary = () => {
    const rows = [
      ["Event Name",  data.event_name],
      ["Event Type",  data.is_event_paid === "E" ? `Paid – $${data.fee}` : "Free"],
      ["When",        `${data.when}  ${data.start_time} – ${data.end_time}`],
      ["Invitee",     data.invitee],
      ["Timezone",    data.invitee_timezone],
      ["Venue",       data.venue],
      ["Host",        data.host],
      ["Duration",    data.event_duration],
    ];
    const tableHtml = `<table>${rows
      .map(([k, v]) => `<tr><td style="font-weight:700;color:#0C4767;padding-right:12px;">${k}</td><td>${v || "—"}</td></tr>`)
      .join("")}</table>`;
    document.getElementById("mc-copy-element").innerHTML = tableHtml;
    const copyHandler = (e) => {
      e.clipboardData.setData("text/html", tableHtml);
      e.clipboardData.setData("text/plain", rows.map(([k, v]) => `${k}: ${v}`).join("\n"));
      e.preventDefault();
    };
    document.addEventListener("copy", copyHandler);
    document.execCommand("copy");
    document.removeEventListener("copy", copyHandler);
    notification.success("Event summary copied successfully");
  };

  /* Build "When" label: date + time + timezone */
  const whenLabel = data
    ? [
        data.when,
        data.start_time && data.end_time ? `${data.start_time} – ${data.end_time}` : null,
        data.host_timezone ? `(${data.host_timezone})` : null,
      ].filter(Boolean).join(" ")
    : "";

  return (
    <>
      <Loader isShowLoader={isLoading} />
      <Modal
        modalClassName="mc-booking-modal"
        isOpen={props.showViewDetailModal && !isLoading}
        toggle={props.handleClose}
        centered
        className="se-details-modal"
        contentClassName="se-details-modal__content"
      >
        <ModalBody>
          <div id="mc-copy-element" className="d-none" />

          {data && (
            <div className="se-details">

              {/* ── Header ──────────────────────────────────────── */}
              <header className="se-details__header">
                <div className="se-details__header-copy">
                  <p className="se-details__eyebrow">Booking details</p>
                  <h2>{data.event_name}</h2>
                  {data.start_time && data.end_time && (
                    <span className="se-details__slot">
                      {data.start_time} – {data.end_time}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="se-details__close"
                  onClick={props.handleClose}
                  aria-label="Close"
                >
                  ×
                </button>
              </header>

              {/* ── 2-column details grid ────────────────────────── */}
              <dl className="se-details__grid">
                <DetailItem label="When"              value={whenLabel}           wide />
                <DetailItem label="Host"              value={data.host} />
                <DetailItem label="Invitee"           value={data.invitee} />
                <DetailItem label="Duration"          value={data.event_duration} />
                <DetailItem label="Invitee time zone" value={data.invitee_timezone} />
                <DetailItem label="Venue"             value={data.venue} />
                <DetailItem label="Venue details"     value={data.venue_details} />
                <DetailItem
                  label="Event type"
                  value={data.is_event_paid === "E" ? `Paid – $${data.fee}` : "Free"}
                />
                <DetailItem label="Invitee instructions" value={data.customer_instruction} wide />
                <DetailItem label="Invitee feedback"     value={data.message_from_customer} wide />
                {(data.question_details || []).map((q, i) => (
                  <DetailItem key={i} label={q.question || "Question"} value={q.answer} wide />
                ))}
              </dl>

              {/* ── Action footer ────────────────────────────────── */}
              <div className="se-details__actions">
                <button
                  type="button"
                  className="se-btn se-btn--ghost"
                  onClick={() => { setShowRescheduleModal(true); onModalOpen(); }}
                >
                  Reschedule
                </button>
                <button
                  type="button"
                  className="se-btn se-btn--ghost"
                  onClick={() => navigate(`/admin/event-summary/${data.event_id}?view-only=true`)}
                >
                  Edit event
                </button>
                <button
                  type="button"
                  className="se-btn se-btn--ghost"
                  onClick={onCopyEventSummary}
                  title="Copy summary"
                >
                  Copy summary
                </button>
                {data && (
                  <PDFDownloadLink document={<BookingDetailPdf data={data} />}>
                    {({ loading }) =>
                      loading ? null : (
                        <button type="button" className="se-btn se-btn--ghost" title="Download PDF">
                          Download PDF
                        </button>
                      )
                    }
                  </PDFDownloadLink>
                )}
                <button
                  type="button"
                  className="se-btn se-btn--danger"
                  onClick={() => setShowCancelModal(true)}
                >
                  Cancel booking
                </button>
              </div>

            </div>
          )}
        </ModalBody>
      </Modal>

      {showCancelModal && (
        <CancelModal
          showCancelModal={showCancelModal}
          handleViewDetailClose={props.handleClose}
          handleClose={hideCancelModal}
          data={data}
          loadBookings={props.loadBookings}
        />
      )}
      {showRescheduleModal && (
        <RescheduleModal
          showRescheduleModal={showRescheduleModal}
          handleViewDetailClose={props.handleClose}
          handleClose={hideRescheduleModal}
          data={data}
          loadBookings={props.loadBookings}
        />
      )}
    </>
  );
};

export default connect(null, {
  getBookingDetails,
})(ViewDetailsModal);
